import React, { useState } from 'react';
import { Insight, getInsightPriorityColor, getInsightIcon } from '../../utils/analyticsInsights';

interface InsightsPanelProps {
  insights: Insight[];
  totalResponses: number;
}

const InsightsPanel: React.FC<InsightsPanelProps> = ({ insights, totalResponses }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  if (insights.length === 0) {
    return null;
  }

  const highPriorityInsights = insights.filter(i => i.priority === 'high');
  const mediumPriorityInsights = insights.filter(i => i.priority === 'medium');
  const lowPriorityInsights = insights.filter(i => i.priority === 'low');

  const displayedInsights = showAll ? insights : insights.slice(0, 6);

  const renderInsightIcon = (iconName: Insight['icon'], color: string) => {
    const path = getInsightIcon(iconName);
    return (
      <svg
        className="w-4 h-4 flex-shrink-0"
        fill="none"
        stroke={color}
        viewBox="0 0 24 24"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={path} />
      </svg>
    );
  };

  return (
    <div
      className="mb-6 rounded-xl overflow-hidden"
      style={{
        backgroundColor: 'white',
        border: '1px solid var(--glass-border-light)',
        boxShadow: 'var(--glass-shadow-sm)',
      }}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between transition-colors hover:bg-gray-50"
        style={{ borderBottom: isExpanded ? '1px solid var(--glass-border-light)' : 'none' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'rgba(199, 161, 74, 0.15)' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="#C7A14A" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div className="text-left">
            <h3
              className="font-semibold"
              style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-primary)', fontSize: '1rem' }}
            >
              AI Insights
            </h3>
            <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
              {insights.length} insights from {totalResponses} responses
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Priority badges */}
          {highPriorityInsights.length > 0 && (
            <span
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#DC2626' }}
            >
              {highPriorityInsights.length} high
            </span>
          )}
          {mediumPriorityInsights.length > 0 && (
            <span
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#B45309' }}
            >
              {mediumPriorityInsights.length} medium
            </span>
          )}

          {/* Expand/Collapse icon */}
          <svg
            className="w-5 h-5 transition-transform"
            style={{
              color: 'var(--color-muted)',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-5">
          {/* High Priority Section */}
          {highPriorityInsights.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4" fill="none" stroke="#DC2626" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#DC2626' }}>
                  Action Required
                </span>
              </div>
              <div
                className="rounded-lg p-3 space-y-2"
                style={{ backgroundColor: 'rgba(220, 38, 38, 0.05)', border: '1px solid rgba(220, 38, 38, 0.2)' }}
              >
                {highPriorityInsights.map((insight) => (
                  <InsightItem key={insight.id} insight={insight} renderIcon={renderInsightIcon} />
                ))}
              </div>
            </div>
          )}

          {/* Key Findings Section */}
          {(mediumPriorityInsights.length > 0 || lowPriorityInsights.length > 0) && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4" fill="none" stroke="#264372" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#264372' }}>
                  Key Findings
                </span>
              </div>
              <div
                className="rounded-lg p-3 space-y-2"
                style={{ backgroundColor: 'rgba(38, 67, 114, 0.03)', border: '1px solid rgba(38, 67, 114, 0.1)' }}
              >
                {displayedInsights
                  .filter(i => i.priority !== 'high')
                  .map((insight) => (
                    <InsightItem key={insight.id} insight={insight} renderIcon={renderInsightIcon} />
                  ))}
              </div>
            </div>
          )}

          {/* Show More / Show Less */}
          {insights.length > 6 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="mt-3 text-sm font-medium transition-colors"
              style={{ color: 'var(--color-accent)' }}
            >
              {showAll ? 'Show less' : `Show all ${insights.length} insights`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface InsightItemProps {
  insight: Insight;
  renderIcon: (iconName: Insight['icon'], color: string) => React.ReactNode;
}

const InsightItem: React.FC<InsightItemProps> = ({ insight, renderIcon }) => {
  const colors = getInsightPriorityColor(insight.priority);

  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 flex-shrink-0">
        {renderIcon(insight.icon, colors.icon)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm leading-relaxed" style={{ wordBreak: 'break-word' }}>
          <span className="font-medium" style={{ color: colors.text }}>
            {insight.title}:
          </span>
          <span className="ml-1" style={{ color: 'var(--color-muted)' }}>
            {insight.description}
          </span>
        </p>
      </div>
    </div>
  );
};

export default InsightsPanel;
